import { Card, CardContent, H4, H5 } from "@sellar-io/components";
import {
  CreditNoteListResponseType,
  InvoiceListResponseType,
} from "@sellar-io/api-v2/types";
import dayjs from "dayjs";
import { ReactElement, useEffect } from "react";
import { useNavigate } from "react-router";
import { twMerge } from "tailwind-merge";
import { useMediaQuery } from "usehooks-ts";
import { useDashboardContext } from "../../../components/dashboard/Dashboard.shared";
import { IntersectingInfiniteLoadingButton } from "../../../components/infiniteLoading/IntersectingInfiniteLoadingButton";
import { NavigationBar } from "../../../components/navigation/NavigationBar";
import { PageContainer } from "../../../components/pageContainer/PageContainer";
import {
  TableLegacy,
  TableLegacyContainer,
  TableLegacyTHead,
  TableLegacyTR,
  TableLegacyTH,
  TableLegacyTBody,
  TableLegacyTD,
} from "../../../components/table/TableLegacy";
import { BUSINESS } from "../../../constants/routes";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import { Mixpanel } from "../../../utils/mixpanel";
import { useInvoiceList } from "../../../hooks/api/v2/invoice/useInvoiceList";
import { formatCurrency } from "@sellar-io/common";
import { useCreditNoteList } from "../../../hooks/api/v2/credit-note/useCreditNoteList";

export const InvoiceSettings = (): ReactElement | null => {
  const navigate = useNavigate();
  const { isTwoPanel } = useDashboardContext();

  const shouldShowTable = useMediaQuery("(min-width: 1455px)");

  const { business: userBusiness } = useSellarCurrentBusiness();

  const supplierId = userBusiness?.Supplier?.id;

  if (!supplierId) throw new Error("Cannot find supplier");

  const invoicesQuery = useInvoiceList({
    limit: 5,
  });

  const creditNotesQuery = useCreditNoteList({
    limit: 20,
  });

  useEffect(() => {
    Mixpanel.track("Invoice Settings Page Viewed");
  }, []);

  return (
    <>
      <NavigationBar
        title={"Invoices"}
        leftIcon={"CHEVRON_LEFT"}
        onClickLeftIcon={isTwoPanel ? undefined : () => navigate(BUSINESS)}
      />
      <div className="mx-auto p-2">
        <PageContainer className={"mx-auto p-6"}>
          <div>
            <Card
              className={twMerge(
                "bg-white",
                "flex flex-col divide-y divide-solid divide-gray-100 align-middle",
              )}
            >
              <CardContent className={"pt-0"}>
                <H5>All invoicing</H5>
                <p>Some description</p>
              </CardContent>
            </Card>
          </div>
        </PageContainer>
      </div>
      <div className={"flex flex-col gap-4 py-4 md:px-0"}>
        {!!invoicesQuery.data &&
          (shouldShowTable ? (
            <>
              {invoicesQuery.data.pages.map((page) =>
                page.map((invoice, i) => (
                  <InvoiceTable key={i} invoice={invoice} />
                )),
              )}
              <IntersectingInfiniteLoadingButton query={invoicesQuery} />
            </>
          ) : (
            invoicesQuery.data.pages.map((page) =>
              page.map((invoice, i) => (
                <InvoiceBox key={i} invoice={invoice} />
              )),
            )
          ))}
        {!!creditNotesQuery.data && (
          <div className="mt-8 flex flex-col">
            <div
              className={
                "mx-auto flex w-full max-w-[900px] flex-col gap-1 overflow-auto px-2 pb-2"
              }
            >
              <H4>Issued credit notes</H4>
              <p>
                Credit notes will automatically be deducted from your next
                invoice
              </p>
            </div>
            <TableLegacyContainer
              className={"mx-auto max-w-[900px] overflow-auto"}
            >
              <TableLegacy>
                <TableLegacyTHead>
                  <TableLegacyTR>
                    <TableLegacyTH>Description</TableLegacyTH>
                    <TableLegacyTH>Subtotal</TableLegacyTH>
                    <TableLegacyTH>Total (inc. VAT)</TableLegacyTH>
                    <TableLegacyTH>Redeemed</TableLegacyTH>
                    <TableLegacyTH>Invoice ID</TableLegacyTH>
                  </TableLegacyTR>
                </TableLegacyTHead>
                <TableLegacyTBody>
                  {creditNotesQuery.data.pages.map((page) =>
                    page.map((creditNote, i) => (
                      <CreditNoteRow key={i} creditNote={creditNote} />
                    )),
                  )}
                  <IntersectingInfiniteLoadingButton query={creditNotesQuery} />
                </TableLegacyTBody>
              </TableLegacy>
            </TableLegacyContainer>
          </div>
        )}
      </div>
    </>
  );
};

const InvoiceTable = ({
  invoice,
}: {
  invoice: InvoiceListResponseType[number];
}) => {
  const hasCreditNotes = invoice.CreditNotes.length > 0;
  const creditTotal = invoice.CreditNotes.reduce(
    (acc, { subtotal }) => acc + subtotal,
    0,
  );

  return (
    <div className="flex flex-col">
      <div
        className={
          "mx-auto flex w-full max-w-[900px] flex-col gap-1 overflow-auto border-l border-zinc-200 px-2 pb-2"
        }
      >
        <span>
          <b>ID:</b> {invoice.customId ?? "DRAFT"}
        </span>
        <span>
          <b>Type:</b> {invoice.type}
        </span>
        <span>
          <b>Status:</b> {invoice.status}
        </span>
        <span>
          <b>Total:</b> {formatCurrency(invoice.subtotal)}
        </span>
        <span>
          <b>Amount due:</b> {formatCurrency(invoice.subtotal - creditTotal)}{" "}
          (Total - credit)
        </span>
      </div>
      <TableLegacyContainer
        className={twMerge(
          "mx-auto max-w-[900px] overflow-auto rounded-tl-none",
          hasCreditNotes && "rounded-bl-none",
        )}
      >
        <TableLegacy>
          <TableLegacyTHead>
            <TableLegacyTR>
              <TableLegacyTH>Description</TableLegacyTH>
              <TableLegacyTH>Unit price</TableLegacyTH>
              <TableLegacyTH>Quantity</TableLegacyTH>
              <TableLegacyTH>Subtotal</TableLegacyTH>
              <TableLegacyTH>Total (inc. VAT)</TableLegacyTH>
            </TableLegacyTR>
          </TableLegacyTHead>
          <TableLegacyTBody>
            {invoice.InvoiceLineItems.map((lineItem, i) => (
              <LineItemRow key={i} lineItem={lineItem} />
            ))}
          </TableLegacyTBody>
        </TableLegacy>
      </TableLegacyContainer>

      {hasCreditNotes && (
        <>
          <div
            className={
              "mx-auto flex w-full max-w-[900px] flex-col overflow-auto border-l border-zinc-200 px-2 py-2"
            }
          >
            <span>
              <b>Applied credit notes</b>
            </span>
          </div>
          <TableLegacyContainer
            className={"mx-auto max-w-[900px] overflow-auto rounded-tl-none"}
          >
            <TableLegacy>
              <TableLegacyTHead>
                <TableLegacyTR>
                  <TableLegacyTH>Date</TableLegacyTH>
                  <TableLegacyTH>Details</TableLegacyTH>
                  <TableLegacyTH>Credit</TableLegacyTH>
                </TableLegacyTR>
              </TableLegacyTHead>
              <TableLegacyTBody>
                {invoice.CreditNotes.map((creditNote, i) => (
                  <TableLegacyTR key={i}>
                    <TableLegacyTD>
                      {dayjs(creditNote.createdAt).format("DD/MM/YYYY")}
                    </TableLegacyTD>
                    <TableLegacyTD>{creditNote.description}</TableLegacyTD>
                    <TableLegacyTD>
                      {formatCurrency(creditNote.subtotal)}
                    </TableLegacyTD>
                  </TableLegacyTR>
                ))}
              </TableLegacyTBody>
            </TableLegacy>
          </TableLegacyContainer>
        </>
      )}
    </div>
  );
};

const InvoiceBox = ({
  invoice,
}: {
  invoice: InvoiceListResponseType[number];
}) => {
  console.log(invoice);
  return <></>;
};

const LineItemRow = ({
  lineItem,
}: {
  lineItem: InvoiceListResponseType[number]["InvoiceLineItems"][number];
}) => {
  return (
    <>
      <TableLegacyTR>
        <TableLegacyTD>{lineItem.description}</TableLegacyTD>
        <TableLegacyTD>{formatCurrency(lineItem.unitPrice)}</TableLegacyTD>
        <TableLegacyTD>{lineItem.quantity}</TableLegacyTD>
        <TableLegacyTD className="font-bold uppercase">
          {formatCurrency(lineItem.subtotal)}
        </TableLegacyTD>
        <TableLegacyTD className="font-bold uppercase">
          {formatCurrency(lineItem.total)}
        </TableLegacyTD>
      </TableLegacyTR>
    </>
  );
};

export const LineItemBox = ({
  lineItem,
}: {
  lineItem: InvoiceListResponseType[number]["InvoiceLineItems"][number];
}) => {
  return (
    <>
      <div className="flex flex-wrap items-center border-b bg-white p-4">
        <div className={"flex flex-col"}>
          <span className="text-sm text-gray-500">ID {lineItem.id}</span>
          <span className="font-bold">
            {lineItem.description} - {formatCurrency(lineItem.subtotal)}
          </span>
          <span>{dayjs(lineItem.createdAt).format("DD/MM/YY")}</span>
        </div>
      </div>
    </>
  );
};

const CreditNoteRow = ({
  creditNote,
}: {
  creditNote: CreditNoteListResponseType[number];
}) => {
  return (
    <>
      <TableLegacyTR>
        <TableLegacyTD>{creditNote.description}</TableLegacyTD>
        <TableLegacyTD className="font-bold uppercase">
          {formatCurrency(creditNote.subtotal)}
        </TableLegacyTD>
        <TableLegacyTD className="font-bold uppercase">
          {formatCurrency(creditNote.total)}
        </TableLegacyTD>
        <TableLegacyTD>{!!creditNote.InvoiceId ? "YES" : "NO"}</TableLegacyTD>
        <TableLegacyTD>{creditNote.InvoiceId}</TableLegacyTD>
      </TableLegacyTR>
    </>
  );
};
